<template>
    <div class="d-flex flex-column justify-center">
        <h3 class="mb-2 font-weight-bold">
            Sponsors editor
        </h3>
        <v-divider class="mb-1" />
        <v-btn
            color="primary"
            class="mt-8 mb-12 align-self-center"
            style="max-width: 250px"
            :disabled="disabled || sponsors.length >= 12"
            @click="addSponsor"
        >
            Add new sponsors
            <v-icon class="ml-3">
                mdi-plus
            </v-icon>
        </v-btn>
        <draggable
            v-model="sponsors"
            class="field-dragger"
            ghost-class="ghost"
            handle=".drag-handle"
            :options="{disabled : disabled}"
        >
            <v-card
                v-for="(sponsor, index) in sponsors"
                :key="`sponsor-${sponsor.id}`"
                class="item-row mb-4 px-2"
            >
                <v-row>
                    <v-col cols="12"
                           lg="1"
                           md="1"
                           sm="1"
                           class="justify-space-around d-flex drag-handle px-0"
                    >
                        <v-icon
                            color="grey"
                        >
                            drag_handle
                        </v-icon>
                    </v-col>
                    <v-col cols="12"
                           lg="3"
                           md="3"
                           sm="3"
                           class="justify-space-around d-flex align-center pr-0"
                    >
                        <avatar-editor v-model="sponsor.avatar"
                                       :disabled="disabled"
                        />
                    </v-col>
                    <v-col cols="12"
                           lg="7"
                           md="7"
                           sm="7"
                    >
                        <v-text-field v-model="sponsor.title"
                                      label="Title"
                                      outlined
                                      dense
                                      hide-details
                                      class="mb-2"
                                      :disabled="disabled"
                        />
                        <v-textarea :value="sponsor.short_description"
                                    label="Short description"
                                    :rules="shortDescriptionRules"
                                    :counter="maxDescriptionLength"
                                    outlined
                                    dense
                                    height="100"
                                    :disabled="disabled"
                                    no-resize
                                    @input="checkLength(index, $event)"
                        />
                        <v-text-field v-model="sponsor.button_title"
                                      label="Button title"
                                      outlined
                                      dense
                                      hide-details
                                      class="mb-2"
                                      :disabled="disabled"
                        />

                        <v-text-field :value="sponsor.button_url"
                                      label="Button URL"
                                      outlined
                                      dense
                                      :rules="urlRules"
                                      class="mb-2"
                                      @input="checkUri(index, $event)"
                        />
                        <v-text-field v-model="sponsor.button_color"
                                      label="Button color"
                                      outlined
                                      dense
                                      hide-details
                                      class="mb-2"
                                      :disabled="disabled"
                        />
                        <v-text-field v-model="sponsor.approval_number"
                                      label="Approval number"
                                      outlined
                                      dense
                                      hide-details
                                      class="mb-2"
                                      :disabled="disabled"
                        />
                    </v-col>
                    <v-col cols="12"
                           lg="1"
                           md="1"
                           sm="1"
                           class="justify-space-around d-flex align-center pl-0"
                    >
                        <v-btn
                            icon
                            color="error"
                            class="mt-8 mb-12"
                            :disabled="disabled"
                            @click="deleteSponsor(sponsor)"
                        >
                            <v-icon>
                                mdi-delete
                            </v-icon>
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card>
        </draggable>
        <sponsor-delete-modal v-if="sponsorToDelete"
                              :sponsor="sponsorToDelete"
                              @input="handleDeleteSponsor"
        />
        <div>
            Footer logo
            <avatar-editor v-model="footerLogo"
                           :disabled="disabled"
                           @input="emitSponsorsFooterLogoChange"
            />
        </div>
    </div>
</template>

<script>
    import draggable from 'vuedraggable';
    import {isHttpUri, isHttpsUri} from 'valid-url';
    import { debounce } from 'lodash';
    import SponsorDeleteModal from '@/components/controls/sponsors/SponsorDeleteModal';
    import { createSponsor } from '@/services/SponsorService';
    import AvatarEditor from '@/components/controls/sponsors/AvatarEditor';


    export default {
        name: 'SponsorsEditor',
        components: {
            AvatarEditor,
            SponsorDeleteModal,
            draggable
        },
        props:{
            value: {
                type: Array,
                default: () => [],
            },
            disabled: {
                type: Boolean,
                default: () => false
            },
            sponsorsFooterLogo: {
                type: Object,
                default: () => ({
                    url: '',
                    key: ''
                })
            }
        },
        data(){
            return {
                sponsors: [...this.value],
                sponsorToDelete: null,
                maxDescriptionLength: 200,
                footerLogo: this.sponsorsFooterLogo
            };
        },
        computed:{
            shortDescriptionRules(){
                return [v => v.length <= this.maxDescriptionLength || `Max ${this.maxDescriptionLength} characters`];
            },
            urlRules(){
                return [v => (isHttpUri(v) !== undefined || isHttpsUri(v) !== undefined  || v.length === 0) || 'URL is invalid'];
            }
        },
        methods:{
            emitSponsors(value){
                this.$emit('input', value);
            },
            emitSponsorsFooterLogoChange(value){
                this.$emit('sponsorsFooterLogoChange', value);
            },
            addSponsor(){
                this.sponsors = [...this.sponsors, createSponsor()];
            },
            deleteSponsor(sponsor) {
                this.sponsorToDelete = sponsor;
            },
            handleDeleteSponsor(value) {
                this.sponsorToDelete = null;
                if (!value) {
                    return;
                }

                this.sponsors = this.sponsors.filter(
                    (s) => s.id !== value.id
                );
            },
            checkLength(index, event){
                if(event.length <= this.maxDescriptionLength){
                    this.sponsors[index].short_description = event;
                }
            },
            checkUri(index, event){
                if(isHttpUri(event) || isHttpsUri(event) || event.length === 0){
                    this.sponsors[index].button_url = event;
                }
            }
        },
        watch: {
            sponsors: {
                deep: true,
                handler: debounce(function (value) {
                    this.emitSponsors(value);
                }, 400),
            }
        },


    };
</script>

<style scoped lang="scss">
@import "../../../_scss/components/colors";

.item-row {
    border: 1px solid transparent;
    border-top: 1px solid #f2f2f2;
    border-left-width: 3px;
    position: relative;

    .drag-handle {
        cursor: grab;
    }

    &.field-active {
        border: 1px solid $primary;
        border-left-width: 3px;
    }

    &.field-error {
        border: 1px solid $error;
        border-left-width: 3px;
    }
}

</style>

<style lang="scss">
</style>
